import React from "react";
import { Link } from "react-router-dom";
import { Eyebrow, Section, Progress, IMAGE_CLASSES } from "../components/SeoUI";
import { DEMO_IMAGES } from "../data/seoData";
import StylistSection from "../components/StylistSection";
import StyleProfileSection from "../components/StyleProfileSection";
import PricingSection from "../components/PricingSection";
import FAQSection from "../components/FAQSection";
import VirtualTryOnSection from "../components/VirtualTryOnSection";
import useSeoMetadata from "../hooks/useSeoMetadata";
import realMatchPercentageSEOImage from "./../assets/real-match-percentage-seo-image.png";

const EvenessHowItWorksSEOPage = () => {
  useSeoMetadata({
    title: "Eveness | Personalised Fashion, Style Match & Virtual Try-On",
    description:
      "Discover fashion tailored to your style profile. Explore personal clothing match scores, curated recommendations and virtual try-on with Eveness.",
    ogTitle: "Eveness | Find Fashion That Fits Your Style",
  });

  return (
    <main className="bg-white text-[#303030] antialiased selection:bg-[#ffd6c5]">
      {/* 1. Match score hero */}
      <Section className="border-b border-neutral-200">
        <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_.85fr] lg:gap-20">
          <div className="mb-0 md:mb-[176px]">
            <Eyebrow>Fit match · Your personal %</Eyebrow>
            <h1 className="max-w-[660px] text-[32px] md:text-[44px] text-[#313131] font-helvena font-medium leading-[48px] tracking-[-2%]">
              A real match percentage for every piece.
            </h1>

            <p className="mt-7 max-w-[600px] font-helvena text-[13px] md:text-[15px] leading-[24px] text-[#5C5C5C]">
              Every item is scored against your body shape, face & neckline and
              colour season — see the exact breakdown before you commit.
            </p>
            <p className="mt-3 text-[14px] text-[#313131] font-helvena md:text-[15px] leading-[100%]">
              The only brand not afraid to say what isn't meant for you.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/"
                className="h-[36px] md:h-[43px] inline-flex items-center justify-center rounded bg-[#F26633] px-6 font-spaceMono md:text-[13px] text-[12px] font-semibold text-white transition hover:bg-[#da4e22]"
              >
                exe_signup
              </Link>
              <Link
                to="https://apps.apple.com/us/app/eveness-ai-fashion-stylist/id6749859788"
                className="h-[36px] md:h-[43px] inline-flex min-h-11 items-center justify-center rounded border border-[#313131] px-5 font-spaceMono md:text-[13px] text-[12px] transition hover:bg-neutral-100"
              >
                Download on App Store ↗
              </Link>
            </div>
            <div className="mt-12 grid max-w-[470px] grid-cols-3 gap-5">
              {[
                ["100,000+", "PIECES INDEXED"],
                ["50", "CURATED PICKS"],
                ["15s", "PER REFRESH"],
              ].map(([v, l]) => (
                <div key={l}>
                  <div className="text-[20px] tracking-tight text-[#313131] font-helvena md:text-[22px] leading-[100%]">
                    {v}
                  </div>
                  <div className="mt-1 font-spaceMono text-[9px] md:text-[11px] tracking-wider text-[#5C5C5C] ">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mx-auto w-full max-w-[440px] rounded-[5px] p-[10px] bg-white shadow-[0px_0px_20px_0px_#0000001A]">
            <div className="flex min-h-[360px] items-center justify-center rounded-[5px] bg-[#F4F2EE] p-8">
              <div className="w-[65%] rounded-md bg-white p-2.5">
                <div className="aspect-[3/4] overflow-hidden rounded bg-[#F4F2EE]">
                  <img
                    src={realMatchPercentageSEOImage}
                    alt="Fashion look shown as an illustrative match example"
                    className={IMAGE_CLASSES}
                  />
                </div>
                <div className="mt-2 font-helvena flex justify-between text-[10px] md:text-[11px] text-black">
                  <span className="font-medium">Reformation</span>
                  <span>$250</span>
                </div>
              </div>
            </div>
            <div className="space-y-4 px-4 py-5">
              <Progress
                label="Body shape · Hourglass"
                value={95}
                color="bg-[#F2C3B4]"
              />
              <Progress
                label="Face & neckline · V-neck"
                value={85}
                color="bg-[#E6A3BF]"
              />
              <Progress
                label="Colour season · Soft Autumn"
                value={92}
                color="bg-[#FFCBC4]"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* 2. Virtual try-on */}
      <VirtualTryOnSection variant="how" />

      {/* 3. Body profile */}
      <StyleProfileSection />

      {/* 4. Catalog */}
      <Section id="catalog">
        <div className="grid gap-6 md:grid-cols-[1.5fr_.5fr] md:items-end">
          <div>
            <Eyebrow>Bin_catalog</Eyebrow>
            <h2 className="font-inter font-medium tracking-[-2%] text-[28px] md:text-[36px] text-[#313131]">
              50 picks · 100K+ pieces · curated in 15s.
            </h2>
          </div>
          <p className="text-[14px] md:text-[15px] font-inter leading-6 text-[#5C5C5C]">
          Eveness is a curated fashion marketplace. We aggregate live inventory from real partner brands, score every item against your body fit and style preferences, and surface the fifty that match you best — refreshed every time you ask.          </p>
        </div>
        <div className="my-10 grid grid-cols-3 border-y border-neutral-200 py-8 text-center">
          {[
            ["100,000+", "PIECES INDEXED"],
            ["50", "CURATED PER FEED"],
            ["15s", "TO REFRESH"],
          ].map(([n, t]) => (
            <div key={t}>
              <div className="text-[#313131] md:text-[42px] text-[30px] font-jetBrainsMono">{n}</div>
              <div className="mt-1 font-jetBrainsMono text-[9px] md:text-[11px] tracking-[8%] text-[#5C5C5C]">
                {t}
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {DEMO_IMAGES.catalog.map((src, i) => (
            <Link to="/marketplace" key={src} className="group block">
              <div className="relative aspect-[.7] overflow-hidden rounded-md bg-neutral-200">
                <img
                  src={src}
                  alt={`Illustrative fashion catalog outfit ${i + 1}`}
                  loading="lazy"
                  className={`${IMAGE_CLASSES} transition duration-500 group-hover:scale-105`}
                />
                <span className="absolute bottom-2 left-2 rounded bg-[#FFFFFFE5] px-2 py-1 font-spaceMono text-[10px] shadow">
                  Find out match %
                </span>
              </div>
              <div className="mt-[5px] flex justify-between gap-2 text-black font-helvena font-medium text-[10px] md:text-[12px]">
                <span>Reformation</span>
                <span>$250</span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* 5. Stylist */}
      <StylistSection />

      {/* 6. Pricing */}
      <PricingSection />

      {/* FAQ and footer */}
      <FAQSection />
    </main>
  );
};

export default EvenessHowItWorksSEOPage;
